import {isValid, parseISO} from 'date-fns';

import {isNil, isNotNil} from 'ramda';

import type {ComposeMessageContext} from '@prompt-bridge/shared/validations';

import type {AiComposerSuggestion} from './AiComposerDraft';

type Fact = ComposeMessageContext['facts'][number];

interface RuleInput {
  facts: Fact[];
  now: number;
}

interface SuggestionRule {
  key: string;
  titleKey: string;
  pick: (input: RuleInput) => Fact | null;
}

const MAX_SUGGESTIONS = 3;
const OPEN_INTEREST_STATES: ReadonlySet<string> = new Set(['NEW', 'OPEN']);
/** Service order states in which the work is done and the vehicle waits for the customer. */
const READY_SERVICE_ORDER_STATES: ReadonlySet<string> = new Set(['WORKDONE', 'INVOICED', 'CLOSED']);
/** Business case states in which the customer still decides about the offer. */
const OPEN_BUSINESS_CASE_STATES: ReadonlySet<string> = new Set(['OFFER', 'CONTRACT']);

const findFact = (facts: Fact[], key: string) => facts.find((fact) => fact.key === key) ?? null;

const toTime = (fact: Fact | null) => {
  if (isNil(fact)) {
    return null;
  }
  const date = parseISO(fact.value);
  return isValid(date) ? date.getTime() : null;
};

const pickFuture = (keys: string[]) => (input: RuleInput) =>
  keys
    .map((key) => findFact(input.facts, key))
    .find((fact) => {
      const time = toTime(fact);
      return isNotNil(time) && time > input.now;
    }) ?? null;

const pickPast = (key: string) => (input: RuleInput) => {
  const fact = findFact(input.facts, key);
  const time = toTime(fact);
  return isNotNil(time) && time <= input.now ? fact : null;
};

const pickPresent = (keys: string[]) => (input: RuleInput) =>
  keys.map((key) => findFact(input.facts, key)).find(isNotNil) ?? null;

const isOpenBusinessCase = (facts: Fact[]) => {
  const state = findFact(facts, 'businessCase.state');
  return isNotNil(state) && OPEN_BUSINESS_CASE_STATES.has(state.value);
};

/**
 * Every offer is tied to the fact it needs. A record without that fact gets no chip; there is no
 * generic fallback, because a chip the model cannot fill with record data produces an empty
 * sentence and costs the employee a click.
 */
const RULES: SuggestionRule[] = [
  {
    key: 'replyToMessage',
    titleKey: 'general.aiMessageComposer.suggestions.replyToMessage.title',
    pick: pickPresent(['replyTo.subject', 'replyTo.from', 'replyTo.receivedAt']),
  },
  {
    key: 'vehicleReady',
    titleKey: 'general.aiMessageComposer.suggestions.vehicleReady.title',
    pick: (input) => {
      const state = findFact(input.facts, 'serviceOrder.state');
      if (isNil(state) || !READY_SERVICE_ORDER_STATES.has(state.value)) {
        return null;
      }
      return pickPast('serviceOrder.completionAt')(input) ?? state;
    },
  },
  {
    key: 'offerValidity',
    titleKey: 'general.aiMessageComposer.suggestions.offerValidity.title',
    pick: (input) =>
      isOpenBusinessCase(input.facts)
        ? pickFuture(['businessCase.onlineOfferValidTo'])(input)
        : null,
  },
  {
    key: 'reservationConfirm',
    titleKey: 'general.aiMessageComposer.suggestions.reservationConfirm.title',
    pick: pickFuture(['activeReservation.reservedUntil']),
  },
  {
    key: 'offerSummary',
    titleKey: 'general.aiMessageComposer.suggestions.offerSummary.title',
    pick: (input) =>
      isOpenBusinessCase(input.facts)
        ? pickPresent(['businessCase.vehiclePriceWithVat', 'businessCase.totalPriceWithVat'])(input)
        : null,
  },
  {
    key: 'testDrive',
    titleKey: 'general.aiMessageComposer.suggestions.testDrive.title',
    pick: (input) => {
      const state = findFact(input.facts, 'businessCase.state');
      return isNotNil(state) && state.value === 'OFFER'
        ? findFact(input.facts, 'vehicle.title')
        : null;
    },
  },
  {
    key: 'visitReminder',
    titleKey: 'general.aiMessageComposer.suggestions.visitReminder.title',
    pick: pickFuture(['workshopInterest.scheduledAt']),
  },
  {
    key: 'rentalReturn',
    titleKey: 'general.aiMessageComposer.suggestions.rentalReturn.title',
    pick: pickFuture(['rentalOrder.end']),
  },
  {
    key: 'nextSteps',
    titleKey: 'entity.activityPanel.activity.nextSteps',
    pick: pickFuture(['serviceOrder.completionAt', 'rentalOrder.start']),
  },
  {
    key: 'priceSummary',
    titleKey: 'general.aiMessageComposer.suggestions.priceSummary.title',
    pick: pickPresent(['serviceOrder.totalPriceWithVat', 'serviceCase.totalPriceWithVat']),
  },
  {
    key: 'interestFollowUp',
    titleKey: 'general.aiMessageComposer.suggestions.interestFollowUp.title',
    // Only once the record carries what the customer asked for; a name and a vehicle alone
    // produce "we would like to offer you the next step", which says nothing.
    pick: (input) => {
      const state = findFact(input.facts, 'interest.state');
      if (isNil(state) || !OPEN_INTEREST_STATES.has(state.value)) {
        return null;
      }
      return findFact(input.facts, 'interest.request');
    },
  },
];

interface Input {
  context: ComposeMessageContext | null;
  translate: (key: string) => string;
  now?: number;
  /** Shows the fact the way the employee reads it; `null` leaves the description out. */
  formatValue?: (fact: Fact) => string | null;
}

const readInstruction = (input: Input, key: string) => {
  const instructionKey = `general.aiMessageComposer.suggestions.${key}.instruction`;
  const instruction = input.translate(instructionKey);
  // A missing translation must not become a billable request containing an i18n key.
  return instruction === instructionKey || instruction.trim().length === 0 ? null : instruction;
};

export function getAiComposerSuggestions(input: Input): AiComposerSuggestion[] {
  if (isNil(input.context)) {
    return [];
  }

  return getFactSuggestions({facts: input.context.facts, now: input.now ?? Date.now()}, input);
}

function getFactSuggestions(ruleInput: RuleInput, input: Input): AiComposerSuggestion[] {
  return RULES.flatMap((rule) => {
    const fact = rule.pick(ruleInput);
    if (isNil(fact)) {
      return [];
    }
    const instruction = readInstruction(input, rule.key);
    if (isNil(instruction)) {
      return [];
    }
    const value = isNotNil(input.formatValue) ? input.formatValue(fact) : fact.value;
    return [
      {
        key: rule.key,
        title: input.translate(rule.titleKey),
        instruction,
        description: isNil(value) ? undefined : `${fact.label}: ${value}`,
      },
    ];
  }).slice(0, MAX_SUGGESTIONS);
}
